<template>
    <div class="editor">
        <slot></slot>
        <div id="toolbar-container" class="editor-toolbar" ref="editorToolbar">
            <div class="tool-set format">
                <dropdown align="top left no-gap" :select="true" :selected="selectedBlockStyle"
                    @selected-changed="onBlockStyleChanged">
                    <button slot="toggle" class="tool has-offset" type="button"><span class="link">{{ $t__('Normal') }}</span></button>

                    <ul>
                        <li><a href="#" @click.prevent="changeFormat('header', '3')" data-option="3" :data-value="$t__('Heading')"><span class="h3">{{ $t__('Heading') }}</span></a></li>
                        <li><a href="#" @click.prevent="changeFormat('header', '5')" data-option="5" :data-value="$t__('Subheading')"><span class="h5">{{ $t__('Subheading') }}</span></a></li>
                        <li><a href="#" class="is-active" @click.prevent="normalizeBlockStyle" data-option="normal" :data-value="$t__('Normal')"><span>{{ $t__('Normal') }}</span></a></li>
                        <li><a href="#" @click.prevent="changeFormat('blockquote', true)" data-option="blockquote" :data-value="$t__('Blockquote')"><q>&#8222;{{ $t__('Blockquote') }}&#8220;</q></a></li>
                        <li><a href="#" @click.prevent="changeFormat('code-block', true)" data-option="code-block" :data-value="$t__('Codeblock')"><span class="monospaced">{{ $t__('Codeblock') }}</span></a></li>
                    </ul>
                </dropdown>
            </div>
            <div class="tool-set">
                <button class="tool ql-bold" :title="$t__('Bold %s', '<cmd+b>')" type="button">
                    <svg viewBox="0 0 18 18"><path class="ql-stroke" d="M5,4H9.5A2.5,2.5,0,0,1,12,6.5v0A2.5,2.5,0,0,1,9.5,9H5A0,0,0,0,1,5,9V4A0,0,0,0,1,5,4Z"></path><path class="ql-stroke" d="M5,9h5.5A2.5,2.5,0,0,1,13,11.5v0A2.5,2.5,0,0,1,10.5,14H5a0,0,0,0,1,0,0V9A0,0,0,0,1,5,9Z"></path></svg>
                </button>

                <button class="tool ql-italic" :title="$t__('Italic %s', '<cmd+i>')" type="button">
                    <svg viewBox="0 0 18 18"><line class="ql-stroke" x1="7" x2="13" y1="4" y2="4"></line><line class="ql-stroke" x1="5" x2="11" y1="14" y2="14"></line><line class="ql-stroke" x1="8" x2="10" y1="14" y2="4"></line></svg>
                </button>

                <button class="tool ql-underline" :title="$t__('Underline %s', '<cmd+u>')" type="button">
                    <svg viewBox="0 0 18 18"><path class="ql-stroke" d="M5,3V9a4.012,4.012,0,0,0,4,4H9a4.012,4.012,0,0,0,4-4V3"></path><rect class="ql-fill" height="1" rx="0.5" ry="0.5" width="12" x="3" y="15"></rect></svg>
                </button>
            </div>

            <div class="tool-set">
                <button class="tool ql-list" value="ordered" type="button">
                    <svg viewBox="0 0 18 18"><line class="ql-stroke" x1="7" x2="15" y1="4" y2="4"></line><line class="ql-stroke" x1="7" x2="15" y1="9" y2="9"></line><line class="ql-stroke" x1="7" x2="15" y1="14" y2="14"></line> <line class="ql-stroke ql-thin" x1="2.5" x2="4.5" y1="5.5" y2="5.5"></line> <path class="ql-fill" d="M3.5,6A0.5,0.5,0,0,1,3,5.5V3.085l-0.276.138A0.5,0.5,0,0,1,2.053,3c-0.124-.247-0.023-0.324.224-0.447l1-.5A0.5,0.5,0,0,1,4,2.5v3A0.5,0.5,0,0,1,3.5,6Z"></path> <path class="ql-stroke ql-thin" d="M4.5,10.5h-2c0-.234,1.85-1.076,1.85-2.234A0.959,0.959,0,0,0,2.5,8.156"></path><path class="ql-stroke ql-thin" d="M2.5,14.846a0.959,0.959,0,0,0,1.85-.109A0.7,0.7,0,0,0,3.75,14a0.688,0.688,0,0,0,.6-0.736,0.959,0.959,0,0,0-1.85-.109"></path></svg>
                </button>

                <button class="tool ql-list" value="bullet" type="button">
                    <svg viewBox="0 0 18 18"> <line class="ql-stroke" x1="6" x2="15" y1="4" y2="4"></line><line class="ql-stroke" x1="6" x2="15" y1="9" y2="9"></line> <line class="ql-stroke" x1="6" x2="15" y1="14" y2="14"></line><line class="ql-stroke" x1="3" x2="3" y1="4" y2="4"></line> <line class="ql-stroke" x1="3" x2="3" y1="9" y2="9"></line> <line class="ql-stroke" x1="3" x2="3" y1="14" y2="14"></line></svg>
                </button>

                <dropdown align="top left no-gap" :select="true" :selected="selectedAlign"
                    @selected-changed="onAlignChanged">
                    <button slot="toggle" class="tool" type="button">
                        <span class="link">
                            <svg viewBox="0 0 18 18"> <line class="ql-stroke" x1="3" x2="15" y1="9" y2="9"></line><line class="ql-stroke" x1="3" x2="13" y1="14" y2="14"></line><line class="ql-stroke" x1="3" x2="9" y1="4" y2="4"></line></svg>
                        </span>
                    </button>

                    <button class="tool is-active" type="button" data-option="normal" @click="changeFormat('align', false)">
                        <svg viewBox="0 0 18 18"> <line class="ql-stroke" x1="3" x2="15" y1="9" y2="9"></line><line class="ql-stroke" x1="3" x2="13" y1="14" y2="14"></line><line class="ql-stroke" x1="3" x2="9" y1="4" y2="4"></line></svg>
                    </button>

                    <button class="tool" type="button" data-option="center" @click="changeFormat('align', 'center')">
                        <svg viewBox="0 0 18 18"> <line class="ql-stroke" x1="15" x2="3" y1="9" y2="9"></line><line class="ql-stroke" x1="14" x2="4" y1="14" y2="14"></line><line class="ql-stroke" x1="12" x2="6" y1="4" y2="4"></line></svg>
                    </button>

                    <button class="tool" type="button" data-option="right" @click="changeFormat('align', 'right')">
                        <svg viewBox="0 0 18 18"> <line class="ql-stroke" x1="15" x2="3" y1="9" y2="9"></line><line class="ql-stroke" x1="15" x2="5" y1="14" y2="14"></line><line class="ql-stroke" x1="15" x2="9" y1="4" y2="4"></line></svg>
                    </button>

                    <button class="tool" type="button" data-option="justify" @click="changeFormat('align', 'justify')">
                        <svg viewBox="0 0 18 18"> <line class="ql-stroke" x1="15" x2="3" y1="9" y2="9"></line><line class="ql-stroke" x1="15" x2="3" y1="14" y2="14"></line><line class="ql-stroke" x1="15" x2="3" y1="4" y2="4"></line></svg>
                    </button>
                </dropdown>
            </div>

            <div class="tool-set">
                <button class="tool" @click="onLinkButtonClick" type="button" :title="$t__('Link')">
                    <svg viewBox="0 0 18 18"> <line class="ql-stroke" x1="7" x2="11" y1="7" y2="11"></line><path class="ql-even ql-stroke" d="M8.9,4.577a3.476,3.476,0,0,1,.36,4.679A3.476,3.476,0,0,1,4.577,8.9C3.185,7.5,2.035,6.4,4.217,4.217S7.5,3.185,8.9,4.577Z"></path><path class="ql-even ql-stroke" d="M13.423,9.1a3.476,3.476,0,0,0-4.679-.36,3.476,3.476,0,0,0,.36,4.679c1.392,1.392,2.5,2.542,4.679.36S14.815,10.5,13.423,9.1Z"></path></svg>
                </button>

                <button class="tool" @click="onAnchorButtonClick" type="button" :title="$t__('Anchor')">
                    <svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="4.45" r="1.95"/><path d="M9 15.5V6.4M4.45 9H2.5c0 3.57 2.93 6.5 6.5 6.5s6.5-2.93 6.5-6.5h-1.95"/></svg>
                </button>

				<button class="tool" @click="onCTAButtonClick" type="button" :title="$t__('Button')">
					<svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd" stroke-linejoin="round" stroke-miterlimit="1.414" fill="currentColor"><path d="M16.111 16H.889C.398 16 0 15.552 0 15V3c0-.552.398-1 .889-1h15.222c.491 0 .889.448.889 1v12c0 .549-.393.995-.889 1zm-1.764-1.991a.669.669 0 0 0 .668-.665V4.721a.666.666 0 0 0-.668-.664H2.653a.666.666 0 0 0-.668.664v8.623c0 .367.299.665.668.665h11.694z"/><path d="M8.15 7.949a1.801 1.801 0 0 0-.713-.765 2.045 2.045 0 0 0-1.047-.268c-.405 0-.767.093-1.086.28-.318.186-.565.45-.741.792a2.487 2.487 0 0 0-.265 1.155c0 .408.087.774.262 1.097.174.323.418.574.732.753.313.178.661.268 1.044.268.392 0 .746-.093 1.063-.277.316-.185.562-.443.739-.776.176-.333.264-.711.264-1.133 0-.42-.084-.795-.252-1.126zm2.057 1.035h.014c.037-.107.077-.204.122-.293l.813-1.705h1.455L11.454 8.99l1.267 2.198h-1.489l-.903-1.776a1.865 1.865 0 0 1-.108-.29h-.014v2.066H8.979V6.986h1.228v1.998zm-3.2.698a.827.827 0 0 1-.265.369.617.617 0 0 1-.383.129.643.643 0 0 1-.553-.287c-.135-.192-.203-.458-.203-.8 0-.35.068-.62.204-.812a.664.664 0 0 1 .572-.287.6.6 0 0 1 .528.299c.129.199.193.472.193.817 0 .221-.031.412-.093.572z"/></svg>
				</button>


                <button id="image-button" class="tool" :title="$t__('Media')" type="button" @click.prevent="openMediaLibrary">
                    <svg viewBox="0 0 18 18" height="18px" width="18px"> <rect class="ql-stroke" height="10" width="12" x="3" y="4"></rect> <circle class="ql-fill" cx="6" cy="7" r="1"></circle><polyline class="ql-even ql-fill" points="5 12 5 11 7 9 8 10 11 7 13 9 13 12 5 12"></polyline></svg>
                </button>

                <button v-if="isAllowed('embed')" class="tool ql-embed" type="button" :title="$t__('Embed Code')">
                    <svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd" stroke-linejoin="round" stroke-miterlimit="1.414" fill="currentColor"><path d="M16.111 16H.889C.398 16 0 15.552 0 15V3c0-.552.398-1 .889-1h15.222c.491 0 .889.448.889 1v12c0 .549-.393.995-.889 1zm-1.764-1.991a.672.672 0 0 0 .668-.665V4.721a.669.669 0 0 0-.666-.664H2.651a.669.669 0 0 0-.666.664v8.623c0 .367.299.665.668.665h11.694z"/><path d="M10.335 5.548a.806.806 0 0 1 .472.192 73.741 73.741 0 0 1 2.758 2.758.776.776 0 0 1 0 1.004 73.741 73.741 0 0 1-2.758 2.758c-.596.508-1.638-.449-1.032-1.088L11.947 9c-.938-.938-2.288-1.948-2.383-2.593-.066-.445.224-.866.771-.859zM6.752 5.55c.59.054.898.838.48 1.278L5.061 9c1.161 1.162 2.747 2.229 2.302 3.057-.216.403-.805.507-1.162.203a73.741 73.741 0 0 1-2.758-2.758.776.776 0 0 1 0-1.004A73.741 73.741 0 0 1 6.2 5.74a.785.785 0 0 1 .551-.19z" fill-rule="nonzero"/></svg>
                </button>
            </div>

            <div class="tool-set">
                <button class="tool ql-clean" :title="$t__('Clear formatting')" type="button">
                    <svg class="" viewBox="0 0 18 18"><line class="ql-stroke" x1="5" x2="13" y1="3" y2="3"></line><line class="ql-stroke" x1="6" x2="9.35" y1="12" y2="3"></line><line class="ql-stroke" x1="11" x2="15" y1="11" y2="15"></line><line class="ql-stroke" x1="15" x2="11" y1="11" y2="15"></line><rect class="ql-fill" height="1" rx="0.5" ry="0.5" width="7" x="2" y="14"></rect></svg>
                </button>
            </div>
        </div>

        <div id="editor-container" class="editor-content"></div>

        <modal class="fullwidth" v-if="showLinkModal" @close="showLinkModal = false" :initial-show="true">
            <template slot="header">{{ linkModalHeader }}</template>
            <template slot="body">
                <div class="form-group">
                    <label class="label">{{ $t__('URL') }}</label><input v-on:keydown.enter.prevent="onLinkModalSubmit" autofocus type="text" v-model="modalWindowData.href">
                </div>
                <div class="form-group">
                    <label class="label">{{ $t__('Text') }}</label><input v-on:keydown.enter.prevent="onLinkModalSubmit" type="text" v-model="modalWindowData.text">
                </div>
                <div class="form-group margin-bottom-none">
                    <label class="checkbox"><input type="checkbox" v-model="modalWindowData.openInNewTab">
                    <span>{{ $t__('Open in new tab') }}</span>
                    </label>
                </div>
            </template>
            <template slot="footer">
                <button
                    :class="['btn', 'medium-sm', 'positive', 'float-right', 'narrow']"
                    @click.prevent="onLinkModalSubmit"
                >
                    {{ $c__('button', 'OK')}}
                </button>
            </template>
        </modal>

        <modal class="fullwidth" v-if="showEmbedModal" @close="showEmbedModal = false" :initial-show="true">
            <template slot="header">{{ $t__('Embed Code') }}</template>
            <template slot="body">
                <div class="form-group">
                    <label for="label" data-label="just label" class="label">{{ $c__("Name of input field where user writes short title or description that helps him to identify specific piece of page's content", 'Label') }}</label>
                    <span class="requirement">{{ $t__('optional') }}</span>
                    <span class="help">{{ $c__('Help text for input field', 'will help you to identify your embeded code') }}</span>
                    <input v-on:keydown.enter.prevent="onEmbedModalSubmit" name="label" type="text" autofocus v-model="embedTitle"/>
                </div>
                <div class="form-group">
                    <label class="label">{{ $t__('Code or URL') }}</label>
                    <span class="help">{{ $c__('Help text for input field', 'html code copied from third party service, e.g. Instagram, or link to content, e.g. YouTube link') }}</span>
                    <textarea v-on:keydown.enter.prevent="onEmbedModalSubmit" class="monospaced" v-model="embedCode" rows="6" required></textarea>
                </div>
            </template>
            <template slot="footer">
                <dropdown v-if="editingEmbed" class="inline-block shadow-heavy" align="bottom left context-width" transition="fade-height-bottom">
                    <button slot="toggle" :class="['btn', 'medium-sm', 'negative', 'float-left', 'narrow']" type="button">
                        {{ $c__('button', 'Delete')}}
                    </button>

                    <div class="title">{{ $t__('Are you sure?') }}</div>

                    <ul>
                        <li>
                            <a href="#" role="button" class="color-negative" @click.prevent="onEmbedModalDelete">
                                {{ $c__('button', 'Delete') }}
                            </a>
                        </li>

                        <li>
                            <a href="#" role="button" @click.prevent>
                                {{ $c__('button', 'Cancel') }}
                            </a>
                        </li>
                    </ul>
                </dropdown>

                <button :disabled="!embedCode"
                    :class="['btn', 'medium-sm', 'positive', 'float-right', 'narrow']"
                    @click.prevent="onEmbedModalSubmit"
                >
                    {{ $c__('button', 'OK')}}
                </button>
            </template>
        </modal>

        <modal class="fullwidth" v-if="showAnchorModal" @close="showAnchorModal = false" :initial-show="true">
            <template slot="header">{{ $t__('Anchor') }}</template>
            <template slot="body">
                <div class="form-group margin-bottom-none columns-gap-custom">
                    <label for="anchor-slug" data-label="just label" class="label">{{ $c__("Name of input field", 'Unique ID') }}</label>
                    <span class="help">{{ $c__('Help text for input field', 'may only contain english latin letters, numbers, dashes and underscores (no spaces are allowed)') }}</span>

                    <div>
                        <div class="columns is-gap-small">
                            <div class="column">
                                <div class="column-item">
                                    <div class="input-group height-100">
                                        <div :class="['addon', 'monospaced', {'focused': anchorSlugHasFocus}]">#anchor-</div>

                                        <input @focus="anchorSlugHasFocus = true" @blur="anchorSlugHasFocus = false" :class="[{'has-error': anchorValidationError}]" ref="anchor-slug" v-on:keydown.enter.prevent="onAnchorModalSubmit" type="text" v-model="anchorSlug"/>
                                    </div>
                                </div>
                            </div>

                            <div class="column is-narrow">
                                <button @click="anchorSlugHasFocus = true" :disabled="!anchorSlugIsValid" v-copy-to-clipboard="{ref: 'anchor-slug', modifier: addHashToAnchorSlug}" class="btn medium-sm height-100">{{ $c__('action to copy e.g. text', 'Copy') }}</button>
                            </div>
                        </div>
                    </div>

                    <height-transition>
                        <div class="validation-text negative" v-if="anchorValidationError">
                            {{ anchorValidationError }}
                        </div>
                    </height-transition>
                </div>

            </template>
            <template slot="footer">
                <dropdown v-if="editingAnchor" class="inline-block shadow-heavy" align="bottom left context-width" transition="fade-height-bottom">
                    <button slot="toggle" :class="['btn', 'medium-sm', 'negative', 'float-left', 'narrow']" type="button">
                        {{ $c__('button', 'Delete')}}
                    </button>

                    <div class="title">{{ $t__('Are you sure?') }}</div>

                    <ul>
                        <li>
                            <a href="#" role="button" class="color-negative" @click.prevent="onAnchorModalDelete">
                                {{ $c__('button', 'Delete') }}
                            </a>
                        </li>

                        <li>
                            <a href="#" role="button" @click.prevent>
                                {{ $c__('button', 'Cancel') }}
                            </a>
                        </li>
                    </ul>
                </dropdown>

                <button :disabled="!anchorSlugIsValid"
                    :class="['btn', 'medium-sm', 'positive', 'float-right', 'narrow']"
                    @click.prevent="onAnchorModalSubmit"
                >
                    {{ $c__('button', 'OK')}}
                </button>
            </template>
        </modal>

    </div>
</template>

<script>
import 'highlight.js/styles/atom-one-light.css'
import Quill from 'quill';
import Modal from './Modal';
import Dropdown from './Dropdown';
import forOwn from 'lodash/forOwn';
import * as utils from '../support/utils';

import MediableMixin from './Mediable';
import EditorConfig from './EditorConfig';

import Delta from 'quill-delta';
let Parchment = Quill.import('parchment');


export default {

     mixins: [
        MediableMixin,
        EditorConfig,
    ],

    data() {
        return {
            html: '',
            editor: null,
            quill: null,
            showModal: false,
            showLinkModal: false,
            showEmbedModal: false,
            showMediaEdit: false,
            selectedMedium: null,
            editedLink: null,
            selectedAlign: "",
            selectedBlockStyle: "",
            modalWindowData: {},
            scrollTimeout: null,
            embedCode: "",
            embedTitle: "",
            editingEmbed: null,
            lastGoodSelection: null,
            showAnchorModal: false,
            editingAnchor: null,
            anchorSlug: "",
            anchorValidationError: "",
            anchorSlugHasFocus: false
        }
    },

    computed: {
        linkModalHeader() {
            if (this.modalWindowData.isButton) {
                return this.$t__('Edit Button');
            }
            return this.$t__('Edit Link');
        },

        anchorSlugIsValid() {
            this.anchorValidationError = "";

            if (!this.anchorSlug) return false;

            if (this.editingAnchor && this.editingAnchor.value().anchor == this.anchorSlug) {
                return true;
            }

            const regex = /^[A-Za-z]+[\w\-\:\.]*$/;
            if (!regex.test(this.anchorSlug)) {
                this.anchorValidationError = this.$t__('ID may only contain english latin letters, numbers, dashes and underscores.');
                return false;
            }

            if (document.getElementById('anchor-'+this.anchorSlug)) {
                this.anchorValidationError = this.$t__('Entered ID is not available.');
                return false;
            }

            return true;
        },
    },

    props: {
        inputId: {type: String},
        forbidden: {type: Array, default: () => []},
        media: {type: Array, default: () => []}
    },

    components: {Modal, Dropdown},

    created() {
    },

    mounted() {
        let vm = this;
        this.$bus.$on('new-image-for-editor', this.addMedia);
        this.$bus.$on('updated-image-in-editor', this.updateSelectedMedia);
        this.$bus.$on('delete-image-from-editor', this.deleteMedia);

        window.addEventListener('scroll', () => {
            window.requestAnimationFrame(this.handleScroll);
        });
        // init editor
        this.editor = new Quill('#editor-container', {
            modules: {
                // syntax: true,
                toolbar: '#toolbar-container',
                anchor: {},
                "embed/focus": true,
            },
            formats: EditorConfig.formats,
            // theme: 'pixenio',
        });

        this.quill = this.editor;

        this.editor.root.innerHTML = document.getElementById(this.inputId).value;

        var toolbar = this.quill.getModule('toolbar');
        toolbar.addHandler('embed', () => {
            this.onEmbedIconClick();
        });

        // filter images from html pasted from clipboard
        this.editor.clipboard.addMatcher('img', function(node, delta) {
            return new Delta();
        });


        if (!document.getElementById(this.inputId).value) {
            this.editor.root.dataset.defaultValue = '';
        } else {
            this.editor.once('text-change', () => { this.editor.root.dataset.defaultValue = this.editor.root.textContent });
        }

        // sync html with hidden input
        this.editor.on('text-change', this.onContentChange);

        // if embed blot is focused update toolbar icon for align
        this.editor.emitter.on('embed-focus', (blot) => {
            let formats = blot.formats();
            if(formats.hasOwnProperty('align')) {
                this.selectedAlign = formats.align;
            } else {
                this.selectedAlign = 'normal';
            }
        });


        this.editor.emitter.on('anchor-clicked', (blot) => {
            this.editingAnchor = blot;
            this.anchorSlug = blot.value().anchor;
            this.showAnchorModal = true;
        });

        this.editor.on('selection-change', this.onSelectionChanged);

        this.editor.container.addEventListener('click', function(e){
            let target = e.target;
            // bubble up until clicked target isn't container itself
            while (!target.isEqualNode(vm.editor.container)) {
                if (target.matches('img')) {
                    vm.onImageClick(target);
                    e.stopPropagation();
                    e.preventDefault();
                    return;
                }
                target = target.parentNode;
            }
        });
    },

    methods: {
        addHashToAnchorSlug(value) {
            return '#anchor-'+value;
        },
        restoreInlineInputRangePosition() {
            if (this.$refs.inlineInput.childNodes[0]) {
                var sel = window.getSelection();
                var slugNode = this.$refs.inlineInput.childNodes[0];
                var offset = this.slugRange.pos - (this.slugRange.end - slugNode.length);
                var range = new Range();
                range.setStart(slugNode, offset <= slugNode.length ? offset : slugNode.length);
                sel.removeAllRanges();
                sel.addRange(range);
            }
        },

        openMediaLibrary() {
            this.$bus.$emit('openMediaLibrary', { supportedMediaTypes: ['unsplash', 'upload'], multiupload: false }, 'content');
        },

        isAllowed(feature) {
            if (this.forbidden.indexOf(feature) > 0) {
                return false;
            }
            return true;
        },

        addMedia(medium) {
            let range = this.editor.getSelection(true);
            if (medium.type == 'icon') {
                let url = 'data:image/svg+xml;utf8,' + medium.content;
                this.editor.insertEmbed(range.index, 'image', {
                    url: url,
                    alt: medium.title,
                });
                this.editor.setSelection(range.index + 2, Quill.sources.SILENT);
            } else {
                let altText = medium.alt_text;
                if (altText === "") {
                    altText = medium.title ?  medium.title : medium.file_name;
                }

                this.editor.insertEmbed(range.index, 'image', {
                    id:  medium.id.toString(),
                    alt: altText,
                    url: medium.url,
                    unsplash_id: medium.unsplash_id
                }, Quill.sources.USER);
                this.editor.setSelection(range.index + 2, Quill.sources.SILENT);
            }
        },

        addVideo(video) {
            let range = this.editor.getSelection(true);
            this.editor.insertEmbed(range.index, 'video', {src:  video.url}, Quill.sources.USER);
            this.editor.setSelection(range.index + 2, Quill.sources.SILENT);

        },

        onLinkButtonClick(e) {
            // reset data in modal window
            this.resetLinkModal();

            if (this.isConvertingTextToLink()) {
                let range = this.editor.getSelection(true);
                this.modalWindowData.text = this.modalWindowData.href = this.editor.getText(range.index, range.length);
            }

            this.openLinkModalWindow();
        },

        onEmbedIconClick() {
            this.embedCode = "";
            this.embedTitle = "";
            this.editingEmbed = false;

            // this will keep focus on editor
            // this.editor.focus();
            this.editor.getSelection(true).length;

            this.showEmbedModal = true;
        },

        onAnchorButtonClick() {
            this.editingAnchor = null;
            this.anchorSlug = "";
            this.showAnchorModal = true;
        },

        createEmbed(embedValue) {
            const externalEmbed = Parchment.create("embed", embedValue);
            const selection = this.lastGoodSelection || this.editor.getSelection(true);
            this.insertBlockBlotAt(this.editor, selection.index, externalEmbed);
            this.editor.update(Quill.sources.USER);
            return externalEmbed;
        },

        insertBlockBlotAt(quill, index, blot) {
            const line = quill.getLine(index)[0];

            // Splitting lines is relative to the line start, the scroll start, so we need to calculate the
            // index within the blot to split at.
            const lineOffset = line.offset(quill.scroll);
            const ref = line.split(index - lineOffset);
            line.parent.insertBefore(blot, ref || undefined);
        },

        onEmbedClick(blot) {
            this.editingEmbed = blot;
            let id = blot.value().embed.id;
            axios.get(`/admin/ajax/embeds/${id}/edit`).then(response => {
                this.embedTitle = blot.value().embed.title;
                this.embedCode = response.data.embed.content;
                this.showEmbedModal = true;
            }).catch(error => {
                //
            });
        },

        onCTAButtonClick(e) {
            this.resetLinkModal();

            if (this.isConvertingTextToLink()) {
                let range = this.editor.getSelection(true);
                this.modalWindowData.text = this.modalWindowData.href = this.editor.getText(range.index, range.length);
            }

            this.modalWindowData.isButton = true;

            this.openLinkModalWindow();
        },

        onSelectionChanged(range, oldRange, source) {
            if (range) {
                document.getElementById('editor-container').parentElement.classList.add('has-focus');
            } else {
                document.getElementById('editor-container').parentElement.classList.remove('has-focus');
                return;
            }
            //TODO: find a better way to reset selected buttons and set active. There might be problem with more formats in the future
            let formats = this.editor.getFormat();
            this.selectedAlign = 'normal';
            this.selectedBlockStyle = 'normal';

            if(formats.hasOwnProperty('align')) {
                this.selectedAlign = formats.align;
            }

            if(formats.hasOwnProperty('header')) {
                this.selectedBlockStyle = formats.header.toString();
            }
            if(formats.hasOwnProperty('blockquote')) {
                this.selectedBlockStyle = 'blockquote';
            }
            if(formats.hasOwnProperty('code-block')) {
                this.selectedBlockStyle = 'code-block';
            }
        },

        onContentChange(delta, oldDelta, source) {
            // sync html from editor to hidden input which will be sent to server
            document.getElementById(this.inputId).value = this.handleEmptyContent(this.editor.root.innerHTML);
            this.$emit('content-changed', this.editor.root.innerHTML);
        },

        isWhitespace(ch) {
            var whiteSpace = false
            if ((ch == ' ') || (ch == '\t') || (ch == '\n')) {
                whiteSpace = true;
            }
            return whiteSpace;
        },

        handleEmptyContent(content) {
            // if content stripped by all tags and trimmed is empty return empty string to prevent unwanted empty lines
            if (content.replace(/\<(?!img|iframe).*?\>/g, "").trim() == "" ) {
                let vm = this;
                // reset all formats and return "really" empty content
                forOwn(vm.editor.getFormat(), function(value, format) {
                    vm.editor.format(format, false);
                });
                return "";
            }
            return content;
        },

        deleteMedia(id) {
            let images = this.editor.container.getElementsByTagName('img');
            for(let i=0; i<images.length; i++) {
                if(images[i].id == id) images[i].remove();
            }
        },

        updateSelectedMedia(medium) {
            let image = this.editor.container.querySelector(`[id = "${medium.id}"]`);
            if (image !== null) {
                let altText = medium.alt_text;
                if (altText === "") {
                    altText = medium.title ?  medium.title : medium.file_name;
                }
                image.alt = altText;
            }
        },

        changeFormat(format, value) {
            if (this.editor.getSelection() == null) return;
            let formats = this.editor.getFormat();
            if(formats.hasOwnProperty(format)) {
                if ((format == 'header' || format == 'align') && formats[format] != value) {
                    // selection has already 'header' format but the level is different
                    this.editor.format(format, value);
                } else {
                    // selection has already selected format - remove this format
                    this.editor.format(format, false);
                    if (format == 'align') {
                        this.selectedAlign = 'normal';
                    } else {
                        this.selectedBlockStyle = 'normal';
                    }
                }
            } else {
                this.editor.format(format, value);
            }
        },

        normalizeBlockStyle() {
            this.editor.format('header', false);
            this.editor.format('code-block', false);
            this.editor.format('blockquote', false);
            this.selectedBlockStyle = 'normal';
        },

        onAlignChanged(newSelectedAlign) {
            this.selectedAlign = newSelectedAlign;
        },

        onBlockStyleChanged(newSelectedBlockStyle) {
            this.selectedBlockStyle = newSelectedBlockStyle;
        },

        onLinkModalSubmit() {
            if (this.isEditingLink()) {
                this.updateEditedLink();
            } else if (this.isConvertingTextToLink()) {
                this.convertTextToLink();
            } else {
                this.addNewLink();
            }
            this.closeLinkModalWindow();
        },

        onEmbedModalSubmit(e) {
            // prevent inserting empty code
            if (!this.embedCode) return;

            e.target.disabled = true;
            e.target.classList.add('is-loading');

            let vm = this;

            // do not save empty embed
            if (vm.embedCode.trim() === "") {
                if (vm.editingEmbed) {
                    let index = vm.editingEmbed.offset(vm.editor.scroll);
                    vm.editingEmbed.remove();
                    vm.editor.setSelection(index);
                }
                vm.editingEmbed = false;
                vm.showEmbedModal = false;
                return;
            }

            let data = {
                content: this.embedCode,
                model: this.model.type,
                modelId: this.model.id
            };



            // this is crucial! getSelection MUST BE called before axios
            // otherwise editor consider lost of user interaction and won't focus back to editor after axios finishes and modal is closed

            if (vm.editingEmbed) {
                // let index = vm.editingEmbed.offset(vm.editor.scroll);
                let range = this.editor.getSelection(true);

                axios.patch(`/admin/ajax/embeds/${vm.editingEmbed.value().embed.id}`, data).then(response => {
                    let formats = this.editingEmbed.formats();

                    vm.editingEmbed.remove();

                    vm.editor.setSelection(range.index);
                    let embed = vm.createEmbed({id: response.data.embed.id, title: vm.embedTitle});
                    if (formats.hasOwnProperty('align')) {
                        embed.format('align', formats['align']);
                    }

                    vm.editingEmbed = false;
                    vm.showEmbedModal = false;
                }).catch(error => {
                    // meh
                });
            } else {
                let range = this.editor.getSelection(true);
                axios.post('/admin/ajax/embeds', data).then((response) => {
                    // vm.editor.insertText(range.index, 'text');
                    let embed = vm.createEmbed({id: response.data.embed.id, title: vm.embedTitle});
                    embed.format('align', 'center');

                    this.editor.setSelection(range.index+1, Quill.sources.USER);
                    vm.showEmbedModal = false;
                }).catch((error) => {
                    // meh
                });
            }
        },

        onAnchorModalSubmit() {
            let index = this.editor.getSelection(true).index;

            if (this.editingAnchor) {
                if (this.editingAnchor.value().anchor == this.anchorSlug) {
                    this.closeAnchorModalWindow();
                    this.editor.focus();
                    return;
                } else {
                    index = this.editor.getIndex(this.editingAnchor);
                    this.editingAnchor.remove();
                }
            }

            this.quill.insertEmbed(index, 'anchor', this.anchorSlug, Quill.sources.USER);
            this.quill.setSelection(index + 1, Quill.sources.USER);

            this.closeAnchorModalWindow();
            this.editor.focus();
        },

        onAnchorModalDelete() {
            let index = this.editor.getIndex(this.editingAnchor);

            this.editingAnchor.remove();
            this.closeAnchorModalWindow();
            this.editor.setSelection(index)
            this.editor.focus();
        },

        onEmbedModalDelete() {
            let index = this.editingEmbed.offset(this.editor.scroll);
            this.editingEmbed.remove();
            this.showEmbedModal = false;
            this.editor.setSelection(index);
            this.editingEmbed = false;
        },

        isEditingLink() {
            return Boolean(this.editedLink);
        },

        isConvertingTextToLink() {
            let length = this.editor.getSelection(true).length;
            return length > 0
        },

        updateEditedLink() {
            // wtf hack - have fun in few months reading this
            if(this.modalWindowData.href) {
                if (this.modalWindowData.isButton) {
                    let index = this.editor.getIndex(this.editedLink);
                    let align = this.editedLink.formats().align;
                    this.editedLink.remove();

                    this.editor.insertEmbed(index, 'cta-button', {
                        href: this.handleUrl(this.modalWindowData.href),
                        class: 'btn',
                        target: this.modalWindowData.openInNewTab ? '_blank' : '_self',
                        text: this.modalWindowData.text ? this.modalWindowData.text : this.handleUrl(this.modalWindowData.href),
                        align: align
                    });
                } else {
                    let index = this.editor.getIndex(this.editedLink);
                    this.editedLink.remove();
                    let text = this.modalWindowData.text ? this.modalWindowData.text : this.handleUrl(this.modalWindowData.href);
                    this.editor.insertText(index, text, 'link', {
                        href: this.handleUrl(this.modalWindowData.href),
                        target: this.modalWindowData.openInNewTab ? '_blank' : '_self',
                        text: ''
                    });
                }
            } else {
                // if url isn't set remove formatting and return plain text or remove button
                if(this.modalWindowData.isButton) {
                    this.editedLink.remove();
                } else {
                    this.editedLink.format('link', false);
                }
            }
            this.editor.focus();
        },

        convertTextToLink() {
            let range = this.editor.getSelection(true);
            this.editor.deleteText(range.index, range.length);

            if (this.modalWindowData.isButton) {
                this.editor.insertEmbed(range.index, 'cta-button', {
                    href: this.handleUrl(this.modalWindowData.href),
                    class: 'btn',
                    target: this.modalWindowData.openInNewTab ? '_blank' : '_self',
                    text: this.modalWindowData.text ? this.modalWindowData.text : this.handleUrl(this.modalWindowData.href)
                });
                this.editor.setSelection(range.index + 2, Quill.sources.SILENT);
            } else {
                let text = this.modalWindowData.text ? this.modalWindowData.text : this.handleUrl(this.modalWindowData.href);
                this.editor.insertText(range.index, text, 'link', {
                    href: this.handleUrl(this.modalWindowData.href),
                    target: this.modalWindowData.openInNewTab ? '_blank' : '_self',
                    text: ''
                });
            }
        },

        addNewLink() {
            let range = this.editor.getSelection(true);
            if (this.modalWindowData.isButton) {

                this.editor.insertEmbed(range.index, 'cta-button', {
                    href: this.handleUrl(this.modalWindowData.href),
                    class: 'btn',
                    target: this.modalWindowData.openInNewTab ? '_blank' : '_self',
                    text: this.modalWindowData.text ? this.modalWindowData.text : this.handleUrl(this.modalWindowData.href)
                });
                this.editor.setSelection(range.index + 2, Quill.sources.SILENT);
            } else {
                let text = this.modalWindowData.text ? this.modalWindowData.text : this.handleUrl(this.modalWindowData.href);
                this.editor.insertText(range.index, text, 'link', {
                    href: this.handleUrl(this.modalWindowData.href),
                    target: this.modalWindowData.openInNewTab ? '_blank' : '_self',
                    text: ''
                });
            }
        },

        openLinkModalWindow() {
            this.showLinkModal = true;
        },

        closeLinkModalWindow() {
            this.showLinkModal = false;
        },

        closeAnchorModalWindow() {
            this.showAnchorModal = false;
            this.editingAnchor = null;
            this.anchorSlug = "";
        },

        resetLinkModal() {
            this.editedLink = null;
            this.modalWindowData = {};
        },

        onImageClick(target) {
            this.$bus.$emit('editor-image-clicked', target.id);
        },

        onLinkClick(blot) {
            let data = {};

            if (blot.statics.blotName == 'cta-button') {
                data = blot.value()['cta-button'];
            } else if (blot.statics.blotName == 'link') {
                data = blot.formats()['link'];
            } else {
                return;
            }

            this.resetLinkModal();
            this.editedLink = blot;

            // load data from link element to modal window
            this.modalWindowData.text = data.text;
            this.modalWindowData.href = data.href;
            this.modalWindowData.openInNewTab = data.target == '_blank';

            if (data.hasOwnProperty('class') && data.class == 'btn') {
                this.modalWindowData.isButton = true;
            }

            this.openLinkModalWindow();
        },

        // lol
        httpize(url) {
            if (!url.match(/^[a-zA-Z]+:\/\//)) {
                url = 'http://' + url;
            }
            return url;
        },

        handleUrl(url) {
            url = url.trim();
            if (utils.isMailto(url)) {
                return url;
            }

            if (utils.isEmail(url)) {
                return 'mailto:' + url;
            }

            if (utils.isUrl(url)) {
                url = this.httpize(url);
                if (utils.isExternalUrl(window.Pixenio.siteUrl, url)) {
                    return url;
                } else {
                    return utils.getRelativeURL(url);
                }
            }

            // url starts with # - we assume it is a hyperlink to content within same page (e.g. heading)
            if (url.substring(0, 1) === '#') {
                return url;
            }

            // add slash at the beginning of the string and return as relative path
            if (url.substring(0, 1) !== '/') {
                url = '/' + url;
            }
            return url;
        },

        handleScroll() {
            var stickyHeader = document.querySelector('.page-header.is-sticky.is-scrolled > .page-header-wrap'),
                editorToolbar = this.$refs.editorToolbar;

            if (!stickyHeader) {
                return;
            };

            if (editorToolbar.getBoundingClientRect().top - stickyHeader.getBoundingClientRect().bottom <= 0) {
                editorToolbar.style.top = stickyHeader.getBoundingClientRect().bottom + 'px';
            } else {
                editorToolbar.style.top = '';
            }

            var calculateOnStickyHeaderChanged = function(e) {
                editorToolbar.style.top = stickyHeader.getBoundingClientRect().bottom + 'px';
                stickyHeader.removeEventListener('transitionend', calculateOnStickyHeaderChanged);
            }

            stickyHeader.addEventListener('transitionend', calculateOnStickyHeaderChanged, false);
        }
    }
}
</script>
